import { motion } from 'framer-motion'
import { Link } from '@tanstack/react-router'
import {
  FileQuestion,
  ServerCrash,
  ShieldOff,
  WifiOff,
  Clock,
  Home,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

interface ErrorPageProps {
  title?: string
  description?: string
  showHomeButton?: boolean
  showBackButton?: boolean
  showRetryButton?: boolean
  onRetry?: () => void
}

// 404 - Not Found
export function NotFoundPage({
  title = 'Page not found',
  description = "Sorry, we couldn't find the page you're looking for.",
  showHomeButton = true,
  showBackButton = true,
}: ErrorPageProps) {
  return (
    <ErrorPageLayout
      icon={<FileQuestion className="h-16 w-16" />}
      code="404"
      title={title}
      description={description}
      showHomeButton={showHomeButton}
      showBackButton={showBackButton}
    />
  )
}

// 403 - Forbidden
export function ForbiddenPage({
  title = 'Access denied',
  description = "You don't have permission to access this resource.",
  showHomeButton = true,
  showBackButton = true,
}: ErrorPageProps) {
  return (
    <ErrorPageLayout
      icon={<ShieldOff className="h-16 w-16" />}
      code="403"
      title={title}
      description={description}
      showHomeButton={showHomeButton}
      showBackButton={showBackButton}
    />
  )
}

// 500 - Server Error
export function ServerErrorPage({
  title = 'Server error',
  description = 'Something went wrong on our end. Please try again later.',
  showHomeButton = true,
  showRetryButton = true,
  onRetry,
}: ErrorPageProps) {
  return (
    <ErrorPageLayout
      icon={<ServerCrash className="h-16 w-16" />}
      code="500"
      title={title}
      description={description}
      showHomeButton={showHomeButton}
      showRetryButton={showRetryButton}
      onRetry={onRetry}
    />
  )
}

// 503 - Service Unavailable
export function MaintenancePage({
  title = 'Under maintenance',
  description = "We're performing scheduled maintenance. Please check back soon.",
}: ErrorPageProps) {
  return (
    <ErrorPageLayout
      icon={<Clock className="h-16 w-16" />}
      code="503"
      title={title}
      description={description}
      showHomeButton={false}
      showRetryButton={true}
      onRetry={() => window.location.reload()}
    />
  )
}

// Offline Page
export function OfflinePage({
  title = "You're offline",
  description = 'Please check your internet connection and try again.',
  showRetryButton = true,
  onRetry,
}: ErrorPageProps) {
  return (
    <ErrorPageLayout
      icon={<WifiOff className="h-16 w-16" />}
      title={title}
      description={description}
      showHomeButton={false}
      showRetryButton={showRetryButton}
      onRetry={onRetry || (() => window.location.reload())}
    />
  )
}

// Base Error Page Layout
interface ErrorPageLayoutProps {
  icon: React.ReactNode
  code?: string
  title: string
  description: string
  showHomeButton?: boolean
  showBackButton?: boolean
  showRetryButton?: boolean
  onRetry?: () => void
}

function ErrorPageLayout({
  icon,
  code,
  title,
  description,
  showHomeButton = true,
  showBackButton = false,
  showRetryButton = false,
  onRetry,
}: ErrorPageLayoutProps) {
  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-md"
      >
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          className="text-muted-foreground/30 mb-8"
        >
          {icon}
        </motion.div>

        {code && (
          <p className="text-7xl font-bold text-muted-foreground/20 mb-4">{code}</p>
        )}

        <h1 className="text-2xl font-bold mb-3">{title}</h1>
        <p className="text-muted-foreground mb-8">{description}</p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {showBackButton && (
            <Button variant="outline" onClick={() => window.history.back()}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          )}
          {showHomeButton && (
            <Button asChild>
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Link>
            </Button>
          )}
          {showRetryButton && (
            <Button variant="outline" onClick={onRetry || (() => window.location.reload())}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  )
}

// Error Fallback for Sentry Error Boundary
export function ErrorFallback() {
  return (
    <ServerErrorPage
      title="Something went wrong"
      description="An unexpected error occurred. Our team has been notified."
      showRetryButton={true}
      onRetry={() => window.location.reload()}
    />
  )
}

// Inline Error Component (for use within pages)
interface InlineErrorProps {
  title?: string
  description?: string
  onRetry?: () => void
  className?: string
}

export function InlineError({
  title = 'Something went wrong',
  description = 'Failed to load data. Please try again.',
  onRetry,
  className = '',
}: InlineErrorProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`text-center py-12 ${className}`}
    >
      <ServerCrash className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
      <h3 className="text-lg font-medium mb-2">{title}</h3>
      <p className="text-muted-foreground mb-4">{description}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      )}
    </motion.div>
  )
}
